import {
    TypicalWeeksChartComponent,
    type TypicalWeekPeriod
} from '@/features/typical-weeks/typical-week-analysis/typical-week-chart/typical-weeks-chart.component';
import type { SensorTimeseriesId } from '@/interfaces/Timeseries.interface';
import {
    TypicalWeekPeriodCode,
    type TypicalWeekPeriod as LegacyPeriod,
    type TypicalWeekPeriodApiFormat
} from '@/interfaces/TypicalWeeks.interface';
import type { AnyCssColor } from '@/libs/shared/ui/theming';
import { API_DATE_FORMAT } from '@/providers/constants.api';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { I18nService } from '@/utils/i18n/i18n.service';
import { AsyncPipe } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import type { TypicalWeeksTileData } from '../../../interfaces/Tile.interface';
import { TilesProvider } from '../../../providers/tiles/tiles.provider';
import { AdvizeoModalService } from '../../../services/advizeo-modal.service';
import { EditTextComponent } from '../../form-controls/controls/edit-text/edit-text';
import { SpawnableTile } from '../spawnable-tile/spawnable-tile.component';

function generatePeriodBounds(periodCode: Exclude<TypicalWeekPeriodCode, TypicalWeekPeriodCode.CUSTOM>): {
    start: string;
    end: string;
} {
    switch (periodCode) {
        case TypicalWeekPeriodCode.LAST_7_DAYS:
            return { start: moment().subtract(8, 'day').format(API_DATE_FORMAT), end: moment().subtract(1, 'day').format(API_DATE_FORMAT) };
        case TypicalWeekPeriodCode.LAST_30_DAYS:
            return {
                start: moment().subtract(31, 'day').format(API_DATE_FORMAT),
                end: moment().subtract(1, 'day').format(API_DATE_FORMAT)
            };
        case TypicalWeekPeriodCode.LAST_365_DAYS:
            return {
                start: moment().subtract(366, 'day').format(API_DATE_FORMAT),
                end: moment().subtract(1, 'day').format(API_DATE_FORMAT)
            };
        default:
            throw new Error('Unknown period code for bounds:: ' + periodCode);
    }
}

interface LegacyState {
    timeseriesId: SensorTimeseriesId | null;
    periods: LegacyPeriod[];
}

@Component({
    styleUrls: ['./tile-typical-weeks.scss'],
    selector: 'tile-typical-weeks',
    templateUrl: 'tile-typical-weeks.html',
    imports: [
        UiSectionComponent,
        UiSectionContentComponent,
        EditTextComponent,
        FormsModule,
        UiIconButtonComponent,
        AsyncPipe,
        TypicalWeeksChartComponent
    ]
})
export class TileTypicalWeeksComponent extends SpawnableTile<TypicalWeeksTileData> {
    private _advizeoModalService = inject(AdvizeoModalService);
    private _translateService = inject(TranslateService);
    private _tilesProvider = inject(TilesProvider);
    private _i18n = inject(I18nService);

    typicalWeekStateLikeData = computed(() => {
        return this.generateStateLikeDataFromTypicalWeeksTileData(this.tileData);
    });

    mappedTypicalWeeksData = computed(() => {
        return this.mapTileDataToState(this.typicalWeekStateLikeData());
    });

    private generateStateLikeDataFromTypicalWeeksTileData(tileData: TypicalWeeksTileData): LegacyState {
        return {
            timeseriesId: tileData.description.timeseriesId,
            periods: tileData.description.periods.map(TileTypicalWeeksComponent.mapTilePeriodToStatePeriod)
        };
    }

    private static mapTilePeriodToStatePeriod(tilePeriod: TypicalWeekPeriodApiFormat): LegacyPeriod {
        return {
            startDate:
                tilePeriod.periodCode === TypicalWeekPeriodCode.CUSTOM
                    ? tilePeriod.startDate!
                    : generatePeriodBounds(tilePeriod.periodCode).start,
            endDate:
                tilePeriod.periodCode === TypicalWeekPeriodCode.CUSTOM
                    ? tilePeriod.endDate!
                    : generatePeriodBounds(tilePeriod.periodCode).end,
            periodCode: tilePeriod.periodCode,
            color: tilePeriod.hexaColor,
            previousPeriodActivated: tilePeriod.lastPeriodActivated,
            previousPeriodColor: tilePeriod.lastPeriodHexaColor,
            previousYearPeriodActivated: tilePeriod.lastYearPeriodActivated,
            previousYearPeriodColor: tilePeriod.lastYearPeriodHexaColor,
            customName: tilePeriod.displayParameters.customName
        };
    }

    private mapTileDataToState(tileData: LegacyState) {
        return {
            timeseriesId: tileData.timeseriesId as SensorTimeseriesId,
            periods: (tileData.periods ?? []).map(p => this.mapPeriodToNewFormat(p))
        };
    }

    private mapPeriodToNewFormat(legacy: LegacyPeriod): TypicalWeekPeriod {
        return {
            name:
                legacy.customName ??
                (legacy.periodCode === TypicalWeekPeriodCode.CUSTOM
                    ? `${Temporal.PlainDate.from(legacy.startDate!).toLocaleString(this._i18n.getLocaleR())} -  ${Temporal.PlainDate.from(legacy.endDate!).toLocaleString(this._i18n.getLocaleR())}`
                    : this._i18n.translateR('PAGES.TYPICAL_WEEKS.PERIOD_CODES.' + legacy.periodCode)),
            range: {
                start: legacy.startDate ? Temporal.PlainDate.from(legacy.startDate) : Temporal.Now.plainDateISO(),
                end: legacy.endDate ? Temporal.PlainDate.from(legacy.endDate) : Temporal.Now.plainDateISO()
            },
            color: legacy.color as AnyCssColor,
            previousPeriodActivated: legacy.previousPeriodActivated,
            previousPeriodColor: legacy.previousPeriodColor as AnyCssColor,

            previousYearPeriodActivated: legacy.previousYearPeriodActivated,
            previousYearPeriodColor: legacy.previousYearPeriodColor as AnyCssColor
        };
    }

    deleteTile(ev: Event) {
        ev.preventDefault();
        ev.stopPropagation();

        this._advizeoModalService.openLegacyDialog({
            title: this._translateService.instant('WIDGET.HOME_TILES.DELETE_CONFIRMATION_TITLE'),
            message: this._translateService.instant('WIDGET.HOME_TILES.DELETE_CONFIRMATION_MESSAGE', { tileName: this.tileData.name }),
            buttons: [
                {
                    text: this._translateService.instant('BUTTONS.CANCEL'),
                    role: 'cancel'
                },
                {
                    text: this._translateService.instant('BUTTONS.DELETE'),
                    handler: () => {
                        this.processDeleteTile();
                    }
                }
            ]
        });
    }

    updateTileName() {
        this._tilesProvider.renameTile(this.tileData.tileId, this.tileData.name);
    }

    private processDeleteTile() {
        this._tilesProvider.deleteTile(this.tileData.tileId, this.tileData.buildingId).then(() => {
            this.deleted.emit();
        });
    }
}
